import { useState } from "react";
import {
  OWNERS, leafTasksUnder, rollupPct, statusOf, toggledStatus, canEditTask,
} from "./util";

function Bar({ pct, small }) {
  return (
    <div className={`wp-bar ${small ? "wp-bar-sm" : ""}`}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

function TaskRow({ node, isAdmin, onStatus, showDoneWhen, renderMeta, onEdit }) {
  const done = statusOf(node) === "done";
  const doing = statusOf(node) === "doing";
  const can = canEditTask(node, isAdmin);
  // Folded by default. A checklist you read standing up has to be one line a
  // row; the done-when is the thing you go and look at when you are not sure
  // whether you are finished, which is not most of the time.
  const [showWhen, setShowWhen] = useState(false);
  return (
    <div
      className={`wp-task ${done ? "done" : ""} ${doing ? "doing" : ""} ${showWhen ? "when-open" : ""}`}
      onClick={() => can && onStatus(node.id, toggledStatus(node))}
      style={!can ? { cursor: "default" } : {}}
      title={can ? "Toggle done" : "Only the owner or Marco can change this"}
      data-testid={`wp-task-${node.id}`}
    >
      <span className={`wp-cb ${done ? "checked" : ""} ${doing ? "doing" : ""}`}>
        {done ? "✓" : doing ? "·" : ""}
      </span>
      <span className="wp-tt">{node.title}</span>
      {/* The `?` is a hover tooltip, which does not exist on a phone. Callers
          whose readers are on phones pass showDoneWhen and get a real button
          that opens the same words below the row. */}
      {node.doneWhen && !showDoneWhen && (
        <span className="wp-donewhen" title={`Done when: ${node.doneWhen}`}>?</span>
      )}
      {node.doneWhen && showDoneWhen && (
        <button
          type="button"
          className={`wp-donewhen wp-donewhen-btn ${showWhen ? "on" : ""}`}
          // The row toggles the checkbox. Asking what done means must not be
          // the same gesture as claiming you are done.
          onClick={(e) => { e.stopPropagation(); setShowWhen((v) => !v); }}
          aria-expanded={showWhen}
          title={showWhen ? "Hide what done means" : "What does done mean?"}
          data-testid={`wp-donewhen-${node.id}`}
        >
          ?
        </button>
      )}
      {node.owner && <span className={`wp-owner o-${node.owner}`}>{OWNERS[node.owner]}</span>}
      {node.est && <span className="wp-est">{node.est}</span>}
      {/* Caller-supplied chips (marketing renders due date and priority here).
          The workplan passes nothing and the row is unchanged. */}
      {renderMeta?.(node)}
      {onEdit && (
        <button
          type="button"
          className="wp-edit"
          // The row itself toggles status. Without this, opening the editor
          // would also tick the box you came to edit.
          onClick={(e) => { e.stopPropagation(); onEdit(node); }}
          title={`Edit "${node.title}"`}
          data-testid={`wp-edit-${node.id}`}
        >
          Edit
        </button>
      )}
      {node.doneWhen && showDoneWhen && showWhen && (
        <span className="wp-donewhen-text">{node.doneWhen}</span>
      )}
    </div>
  );
}

function Epic({ epic, nodes, isAdmin, onStatus, showDoneWhen, renderMeta, onEdit, onAddTask }) {
  const tasks = leafTasksUnder(nodes, epic.id);
  const pct = rollupPct(tasks);
  return (
    <div className="wp-epic" data-testid={`wp-epic-${epic.id}`}>
      <div className="wp-epic-head">
        <span className="wp-epic-name">{epic.title}</span>
        {onEdit && (
          <button type="button" className="wp-edit" onClick={() => onEdit(epic)}
            title={`Edit "${epic.title}"`} data-testid={`wp-edit-${epic.id}`}>Edit</button>
        )}
        <Bar pct={pct} small />
        <span className="wp-pct">{pct}%</span>
      </div>
      {epic.tasks.map((t) => (
        <TaskRow key={t.id} node={t} isAdmin={isAdmin} onStatus={onStatus}
          showDoneWhen={showDoneWhen} renderMeta={renderMeta} onEdit={onEdit} />
      ))}
      {onAddTask && (
        <button type="button" className="wp-add" onClick={() => onAddTask(epic)}
          data-testid={`wp-add-${epic.id}`}>+ Add a task here</button>
      )}
    </div>
  );
}

// Four optional props, every one defaulting to what the Phase 2 workplan
// already does, so that caller is untouched:
//   critLabel    what `critical` means here — the workplan means "Weeks 1-2",
//                marketing means the item that unblocks the most other work
//   showDoneWhen turn the `?` hover tooltip into a button that opens the
//                done-when under the row, because a hover does not exist on a
//                phone and the row has to stay one line at rest
//   renderMeta   extra chips for a task row, supplied by the caller
//   onEdit       show an Edit button on each row and heading
//   onAddTask    show "+ Add a task here" at the foot of each epic
//   showThemeHead  off when the caller draws its own heading for the theme —
//                an event run has one already, and two would be the same name
//                twice
export default function WorkplanList({
  tree, nodes, isAdmin, onStatus, critLabel = "Weeks 1-2", showDoneWhen = false,
  renderMeta = null, onEdit = null, onAddTask = null, showThemeHead = true,
}) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  return (
    <div data-testid="wp-list">
      {tree.map((theme) => {
        const tasks = leafTasksUnder(nodes, theme.id);
        const pct = rollupPct(tasks);
        const isCrit = tasks.some((t) => t.critical);
        // A theme with no head of its own cannot be collapsed by tapping it,
        // so it stays open.
        const open = !showThemeHead || !collapsed[theme.id];
        return (
          <div className="wp-theme" key={theme.id} data-testid={`wp-theme-${theme.id}`}>
            {showThemeHead && (
            <div className="wp-theme-head" onClick={() => toggle(theme.id)}>
              <span className="wp-caret">{open ? "▾" : "▸"}</span>
              <span className="wp-theme-name">{theme.title}</span>
              {onEdit && (
                <button
                  type="button"
                  className="wp-edit"
                  // The heading collapses the theme. Without this, opening the
                  // editor would fold away the thing you are editing.
                  onClick={(e) => { e.stopPropagation(); onEdit(theme); }}
                  title={`Edit "${theme.title}"`}
                  data-testid={`wp-edit-${theme.id}`}
                >
                  Edit
                </button>
              )}
              {isCrit && <span className="wp-crit">{critLabel}</span>}
              <Bar pct={pct} />
              <span className="wp-pct">{pct}%</span>
            </div>
            )}
            {open &&
              theme.epics.map((epic) => (
                <Epic key={epic.id} epic={epic} nodes={nodes} isAdmin={isAdmin}
                  onStatus={onStatus} showDoneWhen={showDoneWhen}
                  renderMeta={renderMeta} onEdit={onEdit} onAddTask={onAddTask} />
              ))}
          </div>
        );
      })}
    </div>
  );
}
